<script setup lang="ts">
/**
 * CHOOSE A PARTY from the active client or case — the list, and nothing else.
 *
 * Extracted from `EntityPartyFill` when batch mode needed the same list for a different purpose
 * (MAR-179). The two surfaces do genuinely different things with the answer:
 *
 *   - the single-run form FILLS a block of inputs with the party's facts;
 *   - the batch table ADDS A ROW per party, one document each.
 *
 * What they must not differ on is which parties are offered, how they are ranked, what is said about
 * each one, and how many show before "ver todos". That is what lives here. Two implementations
 * would drift, and the drift would be a user seeing a party in one place and not the other with no
 * way to tell which was right.
 *
 * ## Coverage is computed with the same function the caller will use
 *
 * The `llena N/M` badge is not a guess: it counts targets `valueForTarget` actually resolves, which
 * is the same call the fill and the row prefill make. A badge that promised more than the click
 * delivered would be worse than no badge — the earlier list showed only who someone was, so two
 * parties looked identical and then one filled nine fields and the other two.
 *
 * Renders NOTHING when the scope is global, the fetch fails, or there is no match. That is the
 * common path and degrading to plain typed fields is correct behaviour, not an error state.
 */
import { ChevronDown, Check, User, Building2, MapPin, Car } from 'lucide-vue-next'
import { isIdentifierProperty } from '~~/shared/entities/vocabulary'
import { valueForTarget } from '~~/shared/entities/partyValues'
import { filterCandidatesByKind, formatPartyMetaLine } from '~/utils/workflowPartyBlocks'
import type { EntityCandidate as PartyCandidate, EntityCandidatesResponse } from '~~/shared/entities/entityCandidate'
import SearchInput from '~/components/controls/SearchInput.vue'

export interface PickerTarget {
  /** `name` | `idNumber` | `attribute:<key>` | `derived:<key>` | `<property>` */
  field: string
  /** Human label, for the "what we know" line. */
  label: string
  /** A `select`'s declared options, so an unmatchable value is not counted as fillable. */
  options?: unknown[]
}

const props = defineProps<{
  /** The identifier category the caller wants, when it wants one. */
  idType?: string | null
  /** The entity kind (`organization` | `person`) this slot accepts, when constrained (MAR-1069). */
  kind?: string | null
  /** What the caller will fill from the chosen party — drives the coverage badge. */
  targets: PickerTarget[]
  /**
   * Parties already used, shown ticked and unclickable.
   *
   * Batch needs this: the user works down the list adding one row each, and a list that gave no sign
   * of what was already added would produce the same employee twice — thirty contracts where two
   * name the same person is exactly the silent duplication this feature must not create.
   */
  usedIds?: string[]
  /** Overrides the default "pick a party" line. */
  title?: string
  /** When true, the caller's own name input drives `search(query)` and no internal search box renders. */
  inlineMode?: boolean
  /** Shows the "Escribir los datos a mano" footer action in inline mode. */
  showManualOption?: boolean
}>()

const emit = defineEmits<{
  select: [candidate: PartyCandidate]
  loaded: [candidates: PartyCandidate[]]
  manual: []
  'no-match': []
}>()

const { t, locale } = useI18n()
const { apiFetch } = useApi()
// The SCOPE, not the rail's payload. `useContextRail` derives its state from an async fetch, so a
// control that mounts with the page reads an empty rail and would silently offer nothing.
const webChat = useWebChatStore()

const scopedCandidates = ref<PartyCandidate[]>([])
const searchCandidates = ref<PartyCandidate[]>([])
/** Whether a query for the current scope has completed — so "not asked yet" and "asked and found
 *  nothing" stop looking identical and the empty line does not flash on every mount. */
const resolved = ref(false)
const searchResolved = ref(false)
let searchSeq = 0

const scopeQuery = computed(() => {
  const scope = webChat.activeScope
  if (scope.kind === 'case') return { caseId: scope.id }
  if (scope.kind === 'client') return { clientId: scope.id }
  return null
})

/** Does the caller want the party's identifier? */
const wantsIdentifier = computed(() => props.targets.some(tgt => isIdentifierProperty(tgt.field)))

const used = computed(() => new Set(props.usedIds ?? []))

function isScopedCandidate(c: PartyCandidate): boolean {
  if (c.provenance.via === 'case') return true
  const rel = c.provenance.relationship
  return rel !== 'organization' && rel !== 'identity' && !rel.startsWith('mentions:')
}

const candidates = computed<PartyCandidate[]>(() => {
  const q = query.value.trim()
  if (q.length >= 1 && searchResolved.value) {
    const lower = q.toLowerCase()
    const scopedMatching = scopedCandidates.value.filter(c =>
      c.name.toLowerCase().includes(lower) || (c.idNumber ?? '').toLowerCase().includes(lower))
    const seen = new Set(scopedMatching.map(c => c.entityId))
    const combined = [
      ...scopedMatching,
      ...searchCandidates.value.filter(c => !seen.has(c.entityId)),
    ]
    return filterCandidatesByKind(combined, props.kind)
  }
  return filterCandidatesByKind(scopedCandidates.value, props.kind)
})

function coverage(c: PartyCandidate): { count: number, labels: string[] } {
  const labels = props.targets.filter(tgt => valueForTarget(c, tgt)).map(tgt => tgt.label)
  return { count: labels.length, labels }
}

/**
 * Scope matches first (AC3), then best-covered first within each tier: with only two rows visible
 * by default, "top" has to mean "most relevant to this scope and most complete".
 */
const ranked = computed(() =>
  [...candidates.value]
    .map(c => ({ c, cov: coverage(c), scoped: isScopedCandidate(c) }))
    .sort((a, b) =>
      (a.scoped === b.scoped ? 0 : a.scoped ? -1 : 1)
      || b.cov.count - a.cov.count
      || a.c.name.localeCompare(b.c.name)))

const query = ref('')
const expanded = ref(false)
const chipsExpanded = ref(false)

/** Search is chrome; below this many parties the list is already scannable and a box would be
 *  clutter on every single-party form. */
const SEARCH_THRESHOLD = 3
/** How many rows before "ver todos". Two is the point of the redesign — the old list rendered every
 *  match as a box and buried the form under them. */
const COLLAPSED = 2
/** How many instant suggestion chips show before "+N más" in inline mode. */
const CHIP_LIMIT = 3

const displayedChips = computed(() =>
  chipsExpanded.value ? ranked.value : ranked.value.slice(0, CHIP_LIMIT))

function kindIcon(kind: string | null | undefined) {
  if (kind === 'organization') return Building2
  if (kind === 'property') return MapPin
  if (kind === 'vehicle') return Car
  return User
}

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return ranked.value
  return ranked.value.filter(({ c }) =>
    c.name.toLowerCase().includes(q) || (c.idNumber ?? '').toLowerCase().includes(q))
})

const visible = computed(() =>
  (expanded.value || query.value.trim() || props.inlineMode) ? filtered.value : filtered.value.slice(0, COLLAPSED))

const hiddenCount = computed(() => Math.max(0, filtered.value.length - visible.value.length))
const showSearch = computed(() => !props.inlineMode && ranked.value.length >= SEARCH_THRESHOLD)

function previewText(cov: { count: number, labels: string[] }): string {
  if (!cov.count) return t('workflowRun.partyFill.noData')
  const shown = cov.labels.slice(0, 3)
  const rest = cov.count - shown.length
  return rest > 0
    ? t('workflowRun.partyFill.fillsPreview', { fields: shown.join(', '), rest })
    : t('workflowRun.partyFill.fillsPreviewAll', { fields: shown.join(', ') })
}

function metaLine(c: PartyCandidate): string {
  return formatPartyMetaLine(c, t, locale?.value, { includeCity: true })
}

async function load() {
  if (!scopeQuery.value && !props.inlineMode) { scopedCandidates.value = []; return }
  try {
    const res = await apiFetch<EntityCandidatesResponse>('/api/entity-candidates', {
      query: {
        ...(scopeQuery.value ?? {}),
        ...(props.inlineMode ? { includeOrganization: 'true' } : {}),
        ...(props.kind ? { kind: props.kind } : {}),
        ...(props.idType ? { idType: props.idType } : {}),
        // A caller that never writes an identifier is answerable by a party whose DUI nobody
        // recorded — requiring one there would hide the very parties the user is looking for.
        // In inline mode (MAR-1069 run form), partial auto-fill (AC6) also offers scoped entities
        // whose `id_number` has not been recorded yet.
        ...(wantsIdentifier.value && !props.inlineMode ? {} : { requireIdentifier: 'false' }),
      },
    })
    scopedCandidates.value = filterCandidatesByKind(res?.candidates ?? [], props.kind)
    resolved.value = true
    emit('loaded', candidates.value)
  } catch {
    scopedCandidates.value = []
  }
}

async function search(rawQuery: string) {
  query.value = rawQuery
  const trimmed = rawQuery.trim()
  if (trimmed.length === 0) {
    searchSeq++
    searchCandidates.value = []
    searchResolved.value = false
    return
  }
  const seq = ++searchSeq
  try {
    const res = await apiFetch<EntityCandidatesResponse>('/api/entity-candidates', {
      query: {
        ...(scopeQuery.value ?? {}),
        includeOrganization: 'true',
        query: trimmed,
        ...(props.kind ? { kind: props.kind } : {}),
        ...(props.idType ? { idType: props.idType } : {}),
        requireIdentifier: 'false',
      },
    })
    if (seq !== searchSeq) return
    searchCandidates.value = filterCandidatesByKind(res?.candidates ?? [], props.kind)
    searchResolved.value = true
    emit('loaded', candidates.value)
    if (trimmed.length >= 2 && filtered.value.length === 0) {
      emit('no-match')
    }
  } catch {
    if (seq !== searchSeq) return
    searchCandidates.value = []
    searchResolved.value = false
  }
}

function onSearchBoxUpdate(val: string) {
  void search(val)
}

// The scope is restored from localStorage after hydration, so on a cold page load it is still
// `global` when this mounts. In inlineMode we preload organization-wide candidates immediately and
// re-load if a client/case scope hydrates from localStorage shortly after.
onMounted(async () => {
  if (props.inlineMode) {
    await load()
    if (scopeQuery.value) return
  }
  for (let attempt = 0; attempt < 5; attempt++) {
    if (scopeQuery.value) { await load(); return }
    await new Promise(resolve => setTimeout(resolve, 150))
  }
})

const emptyMessage = computed(() => {
  if (!resolved.value || candidates.value.length || !scopeQuery.value) return null
  return webChat.activeScope.kind === 'case'
    ? t('workflowRun.suggestions.noneInCase')
    : t('workflowRun.suggestions.noneInClient')
})

const showContainer = computed(() => {
  if (props.inlineMode) {
    if (!query.value.trim()) return false
    return visible.value.length > 0 || searchResolved.value
  }
  return candidates.value.length > 0 || Boolean(emptyMessage.value)
})

defineExpose({ reload: load, search })
</script>

<template>
  <div v-if="showContainer" :class="inlineMode ? 'mt-1.5 rounded-md border border-border bg-card p-2 shadow-xs' : ''">
    <p v-if="!inlineMode" class="text-xs text-muted-foreground">{{ title ?? t('workflowRun.partyFill.title') }}</p>

    <!-- Only once the list is long enough that scanning it is work. -->
    <SearchInput
      v-if="showSearch"
      :model-value="query"
      :placeholder="t('workflowRun.partyFill.searchPlaceholder')"
      size="xs"
      class="mt-2"
      @update:model-value="onSearchBoxUpdate"
    />

    <ul v-if="visible.length" :class="inlineMode ? 'space-y-1' : 'mt-2 space-y-1'">
      <li v-for="{ c, cov } in visible" :key="c.entityId">
        <button
          type="button"
          class="group flex w-full cursor-pointer items-center gap-3 rounded-md border border-input bg-card px-2.5 py-2 text-left transition-colors
                 hover:border-accent-ink hover:bg-accent-lighter focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring
                 disabled:cursor-default disabled:opacity-60 disabled:hover:border-input disabled:hover:bg-card"
          :disabled="used.has(c.entityId)"
          @click="emit('select', c)"
        >
          <Check v-if="used.has(c.entityId)" class="h-3.5 w-3.5 shrink-0 text-success" />
          <span class="min-w-0 flex-1">
            <span class="block truncate text-xs font-medium text-foreground">{{ c.name }}</span>
            <span v-if="metaLine(c)" class="block truncate text-[11px] text-muted-foreground">{{ metaLine(c) }}</span>
            <span
              v-if="filtered.length > 1"
              class="mt-0.5 block truncate text-[11px] text-muted-foreground"
              :title="cov.labels.join(' · ')"
            >{{ previewText(cov) }}</span>
          </span>
          <span
            v-if="!used.has(c.entityId)"
            class="ml-2 hidden shrink-0 text-[11px] font-medium text-accent-ink group-hover:inline group-focus-visible:inline"
          >{{ t('workflowRun.partyFill.useThis') }}</span>

          <span
            class="shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium tabular-nums"
            :class="cov.count ? 'bg-accent-light text-accent-light-foreground' : 'bg-muted text-muted-foreground'"
          >
            {{ t('workflowRun.partyFill.fillsCount', { count: cov.count, total: targets.length }) }}
          </span>
        </button>
      </li>
    </ul>

    <button
      v-if="hiddenCount"
      type="button"
      class="mt-1.5 inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-foreground"
      @click="expanded = true"
    >
      <ChevronDown class="h-3 w-3" />
      {{ t('workflowRun.partyFill.showAll', { count: hiddenCount }) }}
    </button>

    <p v-if="query.trim() && !filtered.length" class="mt-1 text-xs text-muted-foreground/70">
      {{ t('workflowRun.partyFill.noMatch') }}
    </p>
    <p v-else-if="!candidates.length && emptyMessage" class="mt-1 text-xs text-muted-foreground/70">
      {{ emptyMessage }}
    </p>

    <div v-if="inlineMode && showManualOption" class="mt-1.5 border-t border-border pt-1.5">
      <button
        type="button"
        class="w-full cursor-pointer rounded px-2 py-1 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        @click="emit('manual')"
      >
        {{ t('workflowRun.partyFill.writeByHandOption') }}
      </button>
    </div>
  </div>
  <div
    v-else-if="inlineMode && !query.trim() && (ranked.length > 0 || showManualOption)"
    data-party-chips
    class="mt-2 flex flex-wrap items-center justify-between gap-2 text-xs"
  >
    <div v-if="ranked.length" class="flex flex-wrap items-center gap-1.5">
      <span class="mr-0.5 text-muted-foreground">{{ t('workflowRun.partyFill.suggestedLabel') }}</span>
      <button
        v-for="{ c, cov } in displayedChips"
        :key="c.entityId"
        type="button"
        :data-party-chip="c.entityId"
        :disabled="used.has(c.entityId)"
        :title="cov.labels.join(' · ')"
        class="inline-flex cursor-pointer items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-1 text-xs font-medium text-foreground transition-colors hover:border-accent-ink hover:bg-accent-lighter focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default disabled:opacity-60"
        @click="emit('select', c)"
      >
        <component :is="kindIcon(c.kind)" class="h-3 w-3 shrink-0 text-muted-foreground" />
        <span class="max-w-[14rem] truncate">{{ c.name }}</span>
        <span
          class="shrink-0 rounded-full px-1.5 py-0.2 text-[10.5px] font-semibold tabular-nums"
          :class="cov.count ? 'bg-accent-light text-on-accent-light' : 'bg-muted text-muted-foreground'"
        >{{ cov.count }}/{{ targets.length }}</span>
      </button>
      <button
        v-if="!chipsExpanded && ranked.length > CHIP_LIMIT"
        type="button"
        data-party-chips-more
        class="inline-flex cursor-pointer items-center rounded-full border border-dashed border-input px-2 py-1 text-[11px] font-medium text-muted-foreground transition-colors hover:border-accent-ink hover:text-foreground"
        @click="chipsExpanded = true"
      >
        {{ t('workflowRun.partyFill.moreChips', { count: ranked.length - CHIP_LIMIT }) }}
      </button>
    </div>
    <span v-else class="text-muted-foreground">{{ t('workflowRun.partyFill.collapsedHint', { count: targets.length }) }}</span>
    <button
      v-if="showManualOption"
      type="button"
      class="ml-auto cursor-pointer font-medium text-accent-ink hover:underline"
      @click="emit('manual')"
    >
      {{ t('workflowRun.partyFill.writeByHandOption') }}
    </button>
  </div>
  <div
    v-else-if="inlineMode && showManualOption"
    class="mt-1.5 flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground"
  >
    <span>{{ t('workflowRun.partyFill.collapsedHint', { count: targets.length }) }}</span>
    <button
      type="button"
      class="cursor-pointer font-medium text-accent-ink hover:underline"
      @click="emit('manual')"
    >
      {{ t('workflowRun.partyFill.writeByHandOption') }}
    </button>
  </div>
</template>
